// Los motores de imagen que se pueden ELEGIR (`provider_override` de `/v1/ads/generate`).
// Espejo de `IMAGE_PROVIDER_OVERRIDES` en atlas-backend/src/providers/imageChain.ts — el
// backend valida el id con su zod.
//
// Un solo catálogo para los dos lugares que lo ofrecen:
//   • el harness de test masivo (el select "Proveedor" del editor de sets y el resumen del
//     lightbox), donde el elegido corre SOLO, sin fallback: es lo que está midiendo;
//   • "Opciones de modelo avanzadas", adentro del menú del "+" del Agente
//     (ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario), abierto a cualquier cuenta
//     que tenga el Agente, donde el elegido va PRIMERO y la cadena de siempre queda atrás.
// Quién recibe cuál lo decide el backend con el `dev_tester` de la empresa; el front no
// manda esa diferencia.
//
// Sin proveedor (null) corre la cadena de producción: Gemini, y si falla GPT Image 2.5
// Sunburst o Seedream (ADR-2026-09-14-sunburst-es-el-openai-de-la-cadena-de-creacion).
// El Gemini es Nano Banana 2.1 desde 2026-10-16: reemplazó a 3.1 en la cadena y en la
// opción "gemini" de este catálogo (ADR-2026-10-16-nano-banana-2-1-en-2k-por-defecto).
//
// **Los `detalle` los LEE UN CLIENTE.** Describen para qué es bueno cada motor, no qué
// lugar ocupa en nuestra cadena: decían "el primario de producción" y "el fallback", que
// además de filtrar cómo generamos dejó de ser cierto en cuanto el elegido pasó a correr
// con red. Lo que la cadena hace sola se cuenta una vez, en la opción "Atlas".

export type ProveedorDeImagen =
  | 'gemini'
  | 'gpt-image-2.5-flare'
  | 'gpt-image-2.5-sunburst'
  | 'openai'
  | 'seedream'
  | 'recraft'
  | 'gemini-3.1'
  /** Alias viejo de 'gemini' (el backend lo sigue aceptando): lo traen sets del test
   *  masivo guardados antes de que 2.1 fuera el default. No está en el catálogo. */
  | 'nano-banana-2.1';

export interface OpcionProveedorDeImagen {
  id: ProveedorDeImagen;
  label: string;
  /** Una línea: qué modelo es y para qué sirve. */
  detalle: string;
}

/** En el orden en que se muestran, del más general al más específico. */
export const PROVEEDORES_DE_IMAGEN: readonly OpcionProveedorDeImagen[] = [
  {
    id: 'gemini',
    label: 'Nano Banana 2.1',
    detalle: 'El Gemini nuevo: realismo, buen texto y hasta 14 referencias. El más sólido para crear una escena de cero.',
  },
  {
    id: 'gpt-image-2.5-sunburst',
    label: 'GPT Image 2.5 Sunburst',
    detalle: 'El más preciso para EDITAR: cambiar un color, un fondo o una pose sin tocar el resto. Tarda más.',
  },
  {
    id: 'gpt-image-2.5-flare',
    label: 'GPT Image 2.5 Flare',
    detalle: 'El hermano rápido del anterior. Menos control, menos espera.',
  },
  {
    id: 'openai',
    label: 'GPT-5',
    detalle: 'El mejor con texto adentro de la imagen: carteles, precios, placas.',
  },
  {
    id: 'seedream',
    label: 'Seedream 5.0 pro',
    detalle: 'El más permisivo: si otro te rebota una foto por sus reglas de contenido, probá con este.',
  },
  {
    id: 'recraft',
    label: 'Recraft V4.1',
    detalle: 'Sólo para comparar en el harness: no corre en ninguna tarea del agente porque deformaba caras y objetos.',
  },
  {
    id: 'gemini-3.1',
    label: 'Gemini 3.1 Flash Image',
    detalle: 'El Gemini anterior. Queda para comparar contra Nano Banana 2.1.',
  },
];

// El alias viejo sigue siendo un id válido: el backend lo acepta (= 'gemini').
const IDS = new Set<string>([...PROVEEDORES_DE_IMAGEN.map((p) => p.id), 'nano-banana-2.1']);

export function esProveedorDeImagen(v: unknown): v is ProveedorDeImagen {
  return typeof v === 'string' && IDS.has(v);
}

/** El id del catálogo para un proveedor guardado: el alias viejo `nano-banana-2.1` es
 *  'gemini'. Lo usa el select del editor de sets, que con el alias no encontraba opción y
 *  mostraba "Cadena normal" aunque corriera Nano Banana (review atlas-code#187,
 *  ADR-2026-10-17-review-187-el-alias-viejo-se-muestra-como-gemini). */
export function idDeCatalogo<T extends string | null | undefined>(v: T): T | 'gemini' {
  return v === 'nano-banana-2.1' ? 'gemini' : v;
}

export const ETIQUETA_CADENA_NORMAL = 'Cadena normal (con fallback)';

/** Etiqueta para mostrar; sin proveedor, la cadena de producción. */
export function etiquetaProveedorDeImagen(id: ProveedorDeImagen | null | undefined): string {
  if (!id) return ETIQUETA_CADENA_NORMAL;
  const real = id === 'nano-banana-2.1' ? 'gemini' : id;
  return PROVEEDORES_DE_IMAGEN.find((p) => p.id === real)?.label ?? id;
}

/**
 * La opción por defecto del selector del Agente: dejar que elijamos nosotros.
 *
 * Se llama **Atlas** y no "Automático" (pedido de Matías,
 * ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario): "Automático" en una lista de
 * marcas ajenas se lee como una sexta opción rara, mientras que Atlas es la respuesta a
 * "¿quién elige?" y además es la única entrada de la lista que es NUESTRA.
 *
 * **Su detalle no nombra a los otros motores, a propósito.** El default hace exactamente
 * eso —corre Gemini y si falla sigue con OpenAI o Seedream— pero contarlo acá convierte
 * cada generación normal en una lista de proveedores de terceros. Lo que el usuario
 * necesita saber es quién decide y con qué criterio.
 *
 * Vive en este módulo y no en el componente porque es parte del catálogo: el día que la
 * cadena cambie, este texto se revisa junto a los otros cinco y no en un archivo de JSX.
 */
export const MOTOR_AUTOMATICO = {
  label: 'Atlas',
  detalle: 'La mejor opción para cualquier tipo de imagen profesional.',
} as const;

/**
 * Los motores que NO se ofrecen en el Agente, aunque el backend los acepte y el harness los
 * necesite.
 *
 * `openai` es el gpt-5 con el tool de imagen: es el primario de los modos anuncio y una
 * comparación válida en el test masivo, pero como opción suelta en el menú de un cliente no
 * tiene lugar — pedido de Matías. Sale de la LISTA, no del catálogo: `PROVEEDORES_DE_IMAGEN`
 * sigue siendo el espejo de `IMAGE_PROVIDER_OVERRIDES` del backend, y borrarlo de ahí dejaría
 * al harness sin poder probarlo.
 *
 * `recraft` tampoco: deformaba caras y objetos, y desde
 * ADR-2026-10-26-recraft-sale-de-la-generacion no corre en ninguna tarea del agente (sólo en
 * los candidatos de "Creá tu modelo" y en el vectorizado del modo Diseño). Acá queda para
 * que el harness lo pueda comparar.
 *
 * `gemini-3.1` tampoco: es el Gemini anterior. Nano Banana 2.1 lo reemplazó en la cadena y
 * en la opción "Gemini" de este menú (ADR-2026-10-16-nano-banana-2-1-en-2k-por-defecto) —
 * no una segunda entrada de Google al cliente—; queda en el catálogo sólo para que el
 * harness lo siga comparando. El alias `nano-banana-2.1` no está en el catálogo y por eso
 * tampoco en el menú.
 */
const FUERA_DEL_AGENTE: ReadonlySet<ProveedorDeImagen> = new Set(['openai', 'recraft', 'gemini-3.1']);

/** La lista completa como se ve en el Agente: Atlas primero, después los motores. */
export const OPCIONES_DE_MOTOR: ReadonlyArray<{
  id: ProveedorDeImagen | null;
  label: string;
  detalle: string;
}> = [
  { id: null, ...MOTOR_AUTOMATICO },
  ...PROVEEDORES_DE_IMAGEN.filter((p) => !FUERA_DEL_AGENTE.has(p.id)),
];

const IDS_DEL_AGENTE = new Set<string>(
  OPCIONES_DE_MOTOR.map((o) => o.id).filter((id): id is ProveedorDeImagen => id !== null),
);

/**
 * ¿Es un motor que el Agente OFRECE hoy? Es más angosto que `esProveedorDeImagen`, que
 * valida contra el catálogo entero (el espejo del backend, con `openai` adentro para el
 * harness).
 *
 * Lo usa la elección GUARDADA del usuario. Sin esto, quien había elegido un motor antes de
 * que saliera de la lista se quedaba con lo peor de los dos mundos: el selector no le
 * marcaba ninguna opción —el suyo ya no está— pero cada imagen que generaba seguía saliendo
 * por ese `provider_override`, sin forma de verlo ni de cambiarlo salvo eligiendo otro. Un
 * motor que deja de ofrecerse vuelve a Atlas, que es lo que el selector muestra.
 */
export function esMotorDelAgente(v: unknown): v is ProveedorDeImagen {
  return typeof v === 'string' && IDS_DEL_AGENTE.has(v);
}
